import React, { useState, useEffect } from "react";

import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import EditIcon from "@material-ui/icons/Edit";
import Tooltip from "@material-ui/core/Tooltip";
import Drawer from "@material-ui/core/Drawer";
import Link from "@material-ui/core/Link";
import InputLabel from "@material-ui/core/InputLabel";
import Avatar from "@material-ui/core/Avatar";
import Paper from "@material-ui/core/Paper";
import Box from "@material-ui/core/Box";

import { i18n } from "../../translate/i18n";

import ContactDrawerSkeleton from "../ContactDrawerSkeleton";
import MarkdownWrapper from "../MarkdownWrapper";
import ContactModal from "../ContactModal";
import ContactNotesStandalone from "../ContactNotesStandalone";

import { generateColor } from "../../helpers/colorGenerator";
import { getInitials } from "../../helpers/getInitials";
import { getProfilePicUrl } from "../../services/api";

const drawerWidth = 320;

const useStyles = makeStyles(theme => ({
	drawer: {
		width: drawerWidth,
		flexShrink: 0,
	},
	drawerPaper: {
		width: drawerWidth,
		display: "flex",
		borderTop: "1px solid rgba(0, 0, 0, 0.12)",
		borderRight: "1px solid rgba(0, 0, 0, 0.12)",
		borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
		borderTopRightRadius: 4,
		borderBottomRightRadius: 4,
	},
	header: {
		display: "flex",
		borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
		backgroundColor: theme.palette.contactdrawer, //DARK MODE PLW DESIGN//
		alignItems: "center",
		padding: theme.spacing(0, 1),
		minHeight: "73px",
		justifyContent: "flex-start",
	},
	content: {
		display: "flex",
		backgroundColor: theme.palette.contactdrawer, //DARK MODE PLW DESIGN//
		flexDirection: "column",
		padding: "8px 0px 8px 8px",
		height: "100%",
		overflowY: "scroll",
		...theme.scrollbarStyles,
	},

	contactAvatar: {
		margin: 15,
		width: 100,
		height: 100,
	},

	contactHeader: {
		display: "flex",
		padding: "16px 16px 16px 16px",
		flexDirection: "row",
		alignItems: "center",
		justifyContent: "flex-start",
	},

	contactDetails: {
		marginTop: 8,
		padding: 8,
		display: "flex",
		flexDirection: "column",
	},
	contactExtraInfo: {
		marginTop: 4,
		padding: 6,
	},
}));

const ContactDrawer = ({ open, handleDrawerClose, contact, ticket, loading }) => {
	const classes = useStyles();
	const [modalOpen, setModalOpen] = useState(false);
	const [avatarKey, setAvatarKey] = useState(0); // ✅ Key para forzar re-render del avatar cuando cambia la foto

	// ✅ FIX 05/12/2025: Forzar re-render del avatar cuando cambia profilePicUrl o updatedAt del prop contact
	useEffect(() => {
		const profilePicUrl = contact?.profilePicUrl;
		const updatedAt = contact?.updatedAt;
		
		if (profilePicUrl || updatedAt) {
			console.log("[ContactDrawer] 🖼️ Contact cambió, forzando re-render del avatar:", {
				profilePicUrl,
				updatedAt,
				contactId: contact?.id
			});
			// ✅ FIX: Usar updatedAt o timestamp para forzar re-render incluso si profilePicUrl es el mismo
			setAvatarKey(prev => prev + 1);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [contact?.profilePicUrl, contact?.updatedAt]);

	return (
		<>
			<Drawer
				className={classes.drawer}
				variant="persistent"
				anchor="right"
				open={open}
				PaperProps={{ style: { position: "absolute" } }}
				BackdropProps={{ style: { position: "absolute" } }}
				ModalProps={{
					container: document.getElementById("drawer-container"),
					style: { position: "absolute" },
				}}
				classes={{
					paper: classes.drawerPaper,
				}}
			>
				<div className={classes.header}>
					<IconButton onClick={handleDrawerClose}>
						<CloseIcon />
					</IconButton>
					<Typography style={{ flex: 1, textAlign: "center" }}>
						{i18n.t("contactDrawer.header")}
					</Typography>
					<Tooltip title={i18n.t("contactDrawer.buttons.edit")}>
						<IconButton 
							onClick={() => setModalOpen(true)}
							size="small"
							style={{ padding: "6px" }}
						>
							<EditIcon fontSize="small" />
						</IconButton>
					</Tooltip>
				</div>
				{loading ? (
					<ContactDrawerSkeleton classes={classes} />
				) : (
					<div className={classes.content}>
						<Paper square variant="outlined" className={classes.contactHeader}>
							<Box display="flex" alignItems="center" style={{ gap: "12px" }}>
								{/* ✅ FOTO CUADRADA PEGADA AL MARGEN IZQUIERDO */}
								<Avatar
									key={`avatar-${contact?.id}-${avatarKey}-${contact?.updatedAt || ''}`}
									src={contact?.profilePicUrl && !contact.profilePicUrl.includes("nopicture.png") 
										? `${getProfilePicUrl(contact.profilePicUrl)}?t=${avatarKey}-${contact?.updatedAt ? new Date(contact.updatedAt).getTime() : Date.now()}` 
										: null}
									alt="contact_image"
									style={{ 
										width: 64, 
										height: 64, 
										backgroundColor: generateColor(contact?.number || contact?.name || ""), 
										color: "white", 
										fontWeight: "bold",
										borderRadius: "8px",
									}}
								>
									{getInitials(contact?.name || "")}
								</Avatar>
								
								{/* ✅ INFORMACIÓN DEL CONTACTO CON PEQUEÑO ESPACIO */}
								<Box style={{ flex: 1, minWidth: 0 }}>
									{/* ✅ NOMBRE EN NEGRITA */}
									<Typography 
										variant="h6" 
										style={{ 
											fontWeight: "bold",
											marginBottom: 4,
											fontSize: "18px",
										}}
										noWrap
									>
										{contact?.name}
									</Typography>
									
									{/* ✅ NÚMERO DE TELÉFONO MÁS CLARITO */}
									{contact?.number && (
										<Typography 
											variant="body2" 
											style={{ 
												color: "#999999",
												marginBottom: 2,
											}}
											noWrap
										>
											<Link href={`tel:${contact?.number}`} style={{ color: '#999999', textDecoration: 'none' }}>
												{contact?.number}
											</Link>
										</Typography>
									)}
									
									{/* ✅ EMAIL SI EXISTE */}
									{contact?.email && (
										<Typography 
											variant="body2" 
											style={{ 
												color: "#999999",
											}}
											noWrap
										>
											<Link href={`mailto:${contact.email}`} style={{ color: '#999999', textDecoration: 'none' }}>
												{contact.email}
											</Link>
										</Typography>
									)}
								</Box>
							</Box>
						</Paper>
						<Paper square variant="outlined" className={classes.contactDetails}>
							<ContactNotesStandalone contactId={contact?.id} />
						</Paper>
						<Paper square variant="outlined" className={classes.contactDetails}>
							<ContactModal
								open={modalOpen}
								onClose={() => setModalOpen(false)}
								contactId={contact?.id}
								initialValues={{
									name: contact?.name || "",
									number: contact?.number || "",
									email: contact?.email || "",
									extraInfo: contact?.extraInfo || []
								}}
							></ContactModal>
							<Typography variant="subtitle1">
								{i18n.t("contactDrawer.extraInfo")}
							</Typography>
							{contact?.extraInfo?.map(info => (
								<Paper
									key={info.id}
									square
									variant="outlined"
									className={classes.contactExtraInfo}
								>
									<InputLabel>{info.name}</InputLabel>
									<Typography component="div" noWrap style={{ paddingTop: 2 }}>
										<MarkdownWrapper>{info.value}</MarkdownWrapper>
									</Typography>
								</Paper>
							))}
						</Paper>
					</div>
				)}
			</Drawer>
		</>
	);
};

export default ContactDrawer;
